import { useEffect, useState } from 'react';

import Image from 'next/image';

import clsx from 'clsx';

import { AxiosRequestConfig } from 'axios';

import {
  Button as DarriusButton,
  Col,
  Container,
  Row,
  Avatar,
  Typography,
} from 'darrius';

import { imgPrefix } from '../../../utils/imgPrefix';
import { axiosInstance } from '../../../utils/axiosInstance';
import { triggerEvent } from '../../../utils/triggerEvent';

import { showToast } from '../../atoms/Toast/Toast';
import { Dialog } from '../..';

import statusMessageDialog from './messageDialog.json';

import styles from './CIMessageDialog.module.scss';

import { IUserTransformationReturn } from '../../../modules/profile/shared/transformations/user/@interfaces';

interface ICIMessageDialogProps {
  avatar: string;
  name: string;
  category: string;
  user: IUserTransformationReturn;
  isSentMessageSuccess: boolean;
  isMessageDialogOpen: boolean;
  onSendMessage: (isSentMessage: boolean) => void;
  requestUuid: string;
  messageDescription?: string;
  successTitle?: string;
  successDescription?: string;
  phoneNumber?: string;
}

export function CIMessageDialog({
  avatar,
  name,
  category,
  user,
  isSentMessageSuccess,
  isMessageDialogOpen,
  onSendMessage,
  requestUuid,
  messageDescription,
  successTitle,
  successDescription,
  phoneNumber,
}: ICIMessageDialogProps) {
  const [messageValue, setMessageValue] = useState(statusMessageDialog.message);
  const [warningMessage, setWarningMessage] = useState('');

  const [isSentMessageLoading, setIsSentMessageLoading] = useState(false);

  const LOW_CHARACTERS = messageValue.length <= 30;
  const HIGH_CHARACTERS = messageValue.length >= 250;

  const messageClassName = clsx({
    [styles.low_warn]: LOW_CHARACTERS,
    [styles.high_warn]: HIGH_CHARACTERS,
  });

  const borderClassName = clsx({
    [styles.low_warn_border]: LOW_CHARACTERS,
    [styles.div_textarea]: !LOW_CHARACTERS && !HIGH_CHARACTERS,
    [styles.high_warn_border]: HIGH_CHARACTERS,
  });

  const INSTITUTIONAL_PRO = user.partner.isPartnerProfile;
  const VERIFIED_PRO = user.partner.documentationApproved;

  const isInstitutionalPro = clsx({
    [styles.istitutional_pro]: !!INSTITUTIONAL_PRO,
    [styles.verified_pro]: !INSTITUTIONAL_PRO && VERIFIED_PRO,
  });

  function handleSendWhatsApp() {
    const digits = phoneNumber!.replace(/\D/g, '');
    const fullNumber = digits.startsWith('55') ? digits : `55${digits}`;
    const encodedMessage = encodeURIComponent(messageValue);
    window.open(`https://wa.me/${fullNumber}?text=${encodedMessage}`, '_blank');
  }

  async function handleSendMessage() {
    if (phoneNumber) {
      handleSendWhatsApp();
      return;
    }

    try {
      setIsSentMessageLoading(true);

      const requestOptions: AxiosRequestConfig = {
        url: `${process.env.NEXT_PUBLIC_API_PRO}/chat/message`,
        method: 'POST',
        data: {
          profileUserId: user.profileUserId,
          profileId: user.id,
          requestId: user.requestId,
          message: messageValue,
          requestUuid,
        },
      };

      await axiosInstance(requestOptions);

      triggerEvent('send__message-dialog', {
        userId: parseInt(user.id),
        profileId: user.profileUserId,
        requestId: parseInt(user.requestId),
      });

      onSendMessage(true);
    } catch (error) {
      showToast({ message: error.response.data.message, type: 'error' });

      onSendMessage(false);
    } finally {
      setIsSentMessageLoading(false);
    }
  }

  function triggerSuccessModal() {
    triggerEvent('success__message-dialog', {
      userId: parseInt(user.id),
      profileId: user.profileUserId,
      requestId: parseInt(user.requestId),
    });
  }

  const handleChange = (e) => {
    const inputValue = e.target.value;
    setMessageValue(inputValue);

    if (inputValue.length <= 30) {
      setWarningMessage(statusMessageDialog.status.LOW);
    } else if (inputValue.length > 250) {
      setWarningMessage(statusMessageDialog.status.HIGH);
    } else {
      setWarningMessage('');
    }
  };

  useEffect(() => {
    if (!isMessageDialogOpen && messageValue.length === 0) {
      setMessageValue(statusMessageDialog.message);
      setWarningMessage('');
    }
  }, [isMessageDialogOpen, messageValue]);

  return (
    <Dialog.Portal>
      <Dialog.Content className={`messageDialog ${styles.messageDialog}`}>
        {isSentMessageSuccess ? (
          <Container className={`container ${styles.container}`}>
            <Row>
              <Dialog.Close className={`${styles.close}`} />
            </Row>
            <Row>
              <Col className={`col-sm-12 ${styles.messageSucessDialog_col}`}>
                <Image
                  src={imgPrefix('/images/profile/phone-sucess-message.svg')}
                  alt="Mensagem enviada!"
                  title="Mensagem enviada!"
                  width={116}
                  height={116}
                  className={styles.image}
                />
                <Typography.Subtitle size="m">
                  {successTitle}
                </Typography.Subtitle>
                <Typography.Text className={styles['success-message-text']}>
                  {successDescription}
                </Typography.Text>
                <Dialog.Trigger asChild>
                  <DarriusButton.Root
                    onClick={triggerSuccessModal}
                    variation="primary"
                    fullWidth
                  >
                    <span>Concluir</span>
                  </DarriusButton.Root>
                </Dialog.Trigger>
              </Col>
            </Row>
          </Container>
        ) : (
          <Container className={`container ${styles.container}`}>
            <Row>
              <Dialog.Close className={`${styles.close}`} />
            </Row>
            <Row>
              <Col className="col-sm-12">
                <p className={`title ${styles.title}`}>Enviar mensagem</p>
                <Row>
                  <Col className="col-sm-3">
                    <Avatar
                      src={avatar}
                      alt="Imagem do profissional"
                      size="60px"
                      className={styles['profile-avatar']}
                      fallback={{
                        variant: 'byName',
                        name,
                      }}
                    />
                  </Col>

                  <Col className={`${isInstitutionalPro} col-sm-9`}>
                    <Typography.Subtitle size="m" className={styles.name}>
                      {name}
                    </Typography.Subtitle>
                    <Typography.Text className={styles.category}>
                      {category}
                    </Typography.Text>
                  </Col>
                </Row>

                <Row className={styles.description}>
                  <Col className="col-sm-12">
                    <Typography.Text className={styles.text}>
                      {messageDescription}
                    </Typography.Text>
                  </Col>
                </Row>

                <Row>
                  <Col
                    className={`${styles['label-textarea']} label-textarea col-sm-12`}
                  >
                    <p className={messageClassName}>Mensagem</p>
                    <div className={borderClassName}>
                      <textarea
                        value={messageValue}
                        onChange={handleChange}
                        name="textarea"
                        rows={5}
                        cols={40}
                      />
                      <div className={styles.characters}>
                        {messageValue.length == 0 || messageValue.length > 250
                          ? '250'
                          : messageValue.length}
                      </div>
                    </div>
                    {warningMessage ? (
                      <p className={messageClassName}>{warningMessage}</p>
                    ) : (
                      ''
                    )}
                  </Col>
                </Row>

                <DarriusButton.Root
                  fullWidth
                  size="small"
                  disabled={isSentMessageLoading || !!warningMessage}
                  isLoading={isSentMessageLoading}
                  onClick={handleSendMessage}
                >
                  Enviar mensagem
                </DarriusButton.Root>
              </Col>
            </Row>
          </Container>
        )}
      </Dialog.Content>
    </Dialog.Portal>
  );
}
